import React, { useState, useRef, useEffect } from 'react'
import { BiChevronDown } from 'react-icons/bi'
import { AiOutlineSearch } from 'react-icons/ai'
import { MdAddBox } from 'react-icons/md'
import { HiCheck } from 'react-icons/hi'
import { useCurrent } from '../../hooks'

const MwSelector = ({
  label,
  _data,
  dataType,
  onClickNew,
  setSelectedItem,
  selectedItem,
  initalValue = 'Choose',
  withLabel = true,
  withAddNew = true,
  className,
  styleList,
  invalid,
  invalidMsg,
}) => {
  const [inputValue, setInputValue] = useState('')
  const { currentLangId } = useCurrent()
  const [open, setOpen] = useState(false)
  const buttonRef = useRef(null)
  const dropdownRef = useRef(null)
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 })

  useEffect(() => {
    if (open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      setDropdownPosition({ top: rect.bottom + 4, left: rect.left, width: rect.width })
    }
  }, [open])

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        open &&
        buttonRef.current && !buttonRef.current.contains(e.target) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target)
      ) setOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  const onSelectedHandler = (item) => {
    setSelectedItem({ ...item, label: item.label })
    setOpen(false)
    setInputValue('')
  }

  const filteredData = _data?.filter((item) => {
    if (dataType === 'labelValue') {
      if (item?.lang === currentLangId)
        return item?.label?.toLowerCase().startsWith(inputValue)
      return item?.label?.toLowerCase().includes(inputValue.toLowerCase())
    }
    return true
  }) ?? []

  const currentLabel = selectedItem && selectedItem !== 'sub' ? selectedItem.label : null
  const currentDot = selectedItem && selectedItem !== 'sub' ? (selectedItem.dot || selectedItem.color || '') : ''

  return (
    <div className={`relative ${className}`}>
      {withLabel && label && (
        <label className="block text-xs font-medium text-slate-500 mb-1">
          {label}
        </label>
      )}

      <div
        ref={buttonRef}
        onClick={() => setOpen(!open)}
        className={`
          flex items-center gap-2 h-[38px] px-3 w-full
          bg-white border rounded-lg cursor-pointer
          transition-all duration-150
          ${open
            ? 'border-indigo-400 ring-2 ring-indigo-100'
            : invalid
              ? 'border-red-400 ring-2 ring-red-100'
              : 'border-slate-200 hover:border-slate-300'
          }
          ${styleList}
        `}
      >
        {currentDot ? (
          <span
            className="w-2 h-2 rounded-full flex-shrink-0"
            style={{ background: String(currentDot) }}
          />
        ) : null}
        <span className={`flex-1 text-sm text-start ${currentLabel ? 'text-slate-700' : 'text-slate-400'}`}>
          {currentLabel || initalValue}
        </span>
        <BiChevronDown
          className={`flex-shrink-0 text-slate-400 text-lg transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </div>

      {invalid && invalidMsg && (
        <p className="mt-1 text-xs text-red-500">{invalidMsg}</p>
      )}

      {open && (
        <div
          ref={dropdownRef}
          className="fixed z-[9999] bg-white rounded-lg border border-slate-200 shadow-lg overflow-hidden"
          style={{ top: `${dropdownPosition.top}px`, left: `${dropdownPosition.left}px`, width: `${dropdownPosition.width}px` }}
        >
          <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-100 bg-slate-50">
            <AiOutlineSearch className="text-slate-400 text-base flex-shrink-0" />
            <input
              autoFocus
              type="text"
              className="flex-1 text-sm bg-transparent border-none outline-none text-slate-700 placeholder-slate-400"
              placeholder="Search…"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value.toLowerCase())}
            />
            {inputValue && filteredData.length > 0 && (
              <span className="text-xs text-slate-400 flex-shrink-0 tabular-nums">
                {filteredData.length}
              </span>
            )}
            {withAddNew && onClickNew && (
              <button
                type="button"
                onClick={() => { onClickNew(); setOpen(false) }}
                className="text-indigo-500 hover:text-indigo-700 transition-colors flex-shrink-0"
                title="Add new"
              >
                <MdAddBox className="text-xl" />
              </button>
            )}
          </div>

          <div className="max-h-56 overflow-y-auto divide-y divide-slate-50">
            {filteredData.length > 0 ? (
              filteredData.map((item, index) => {
                const isActive = selectedItem?.value === item.value
                return (
                  <div
                    key={index}
                    className={`
                      flex items-center gap-2 px-3 py-2 text-sm cursor-pointer transition-colors
                      ${isActive
                        ? 'text-indigo-600 font-medium bg-indigo-50'
                        : 'text-slate-700 hover:bg-slate-50'
                      }
                    `}
                    onClick={() => onSelectedHandler(item)}
                  >
                    <span className={`w-4 h-4 flex-shrink-0 transition-opacity ${isActive ? 'opacity-100' : 'opacity-0'}`}>
                      <HiCheck className="w-4 h-4 text-indigo-600" />
                    </span>
                    {(item?.dot || item?.color) ? (
                      <span
                        className="w-2 h-2 rounded-full flex-shrink-0"
                        style={{ background: String(item.dot || item.color) }}
                      />
                    ) : null}
                    {item.label}
                  </div>
                )
              })
            ) : inputValue ? (
              <div className="flex flex-col items-center gap-2 py-6 px-4">
                <span className="text-2xl select-none">🔍</span>
                <p className="text-xs text-slate-400 text-center">
                  No results for{' '}
                  <span className="font-medium text-slate-600">"{inputValue}"</span>
                </p>
                {withAddNew && onClickNew && (
                  <button
                    type="button"
                    onClick={() => { onClickNew(); setOpen(false) }}
                    className="mt-1 text-xs text-indigo-600 font-medium hover:underline"
                  >
                    + Add "{inputValue}" as new
                  </button>
                )}
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  )
}

export default MwSelector
